import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import FormCheck from 'react-bootstrap/FormCheck';
import FormControl from 'react-bootstrap/FormControl';
import FormGroup from 'react-bootstrap/FormGroup';
import FormLabel from 'react-bootstrap/FormLabel';
import DataPopover from 'components/DataPopover';
import TextButton from 'components/TextButton';
import { ERROR_REQUIRED } from 'utils/messages';
import MoreInfoIcon from 'resources/UI_ButtonInfo.png';
import './Input.css';

const getValidationState = (touched, error, warning) =>
  touched && error ? 'error' : touched && warning ? 'warning' : null;

function Input({
  label,
  labelNode,
  subLabel,
  type,
  input,
  meta,
  validationState = undefined,
  hideErrors = false,
  hideWarnings = false,
  hideRequired = false,
  controlWrapperFn,
  disabled = false,
  disableOnSubmitting = false,
  tooltip,
  tooltipContainer,
  inputMutator,
  children,
  wrapperClass,
  icon,
  ...otherProps
}) {
  const { touched, error, warning, submitting } = meta || {};

  if (validationState === undefined) {
    validationState = getValidationState(touched, error, warning);
  }

  let disable = disabled || (disableOnSubmitting && submitting);

  if (inputMutator) {
    input = Object.assign({}, input, inputMutator(input));
  }

  const name = (input && input.name) || otherProps.name;
  const id =
    otherProps.id || (input && input.id) || `wccinput-${type || ''}-${name}`;
  const controlProps = { id: id };

  let labelElement;
  if (tooltip) {
    const tooltipId = id + '-tooltip';
    labelElement = (
      <Fragment>
        <FormLabel htmlFor={id}>{labelNode || label}</FormLabel>
        <DataPopover
          tooltipMessage={tooltip}
          tooltipClass="input-tooltip"
          placement="top"
          trigger="click"
          container={tooltipContainer}
          rootClose
        >
          <TextButton
            className="tooltip-trigger"
            aria-hidden="true"
            aria-label={`${label} help`}
          >
            <img src={MoreInfoIcon} alt="" />
          </TextButton>
        </DataPopover>
        <span className="sr-only" id={tooltipId}>
          {tooltip}
        </span>
      </Fragment>
    );
    controlProps['aria-describedby'] = tooltipId;
  } else {
    labelElement = <FormLabel htmlFor={id}>{labelNode || label}</FormLabel>;
  }

  let helpBlock = null;
  if (touched) {
    if (error && !hideErrors && (!hideRequired || error !== ERROR_REQUIRED)) {
      const errorId = id + '-error';
      helpBlock = (
        <Alert role="alert" id={errorId} variant="danger">
          <span className="sr-only">
            {label} error: {error}
          </span>
          <span aria-hidden="true">{error}</span>
        </Alert>
      );
      controlProps['aria-invalid'] = 'true';
      controlProps['aria-describedby'] = errorId;
    } else if (warning && !hideWarnings) {
      const warningId = id + '-warning';
      helpBlock = (
        <Alert role="alert" id={warningId} variant="warning">
          <span className="sr-only">
            {label} warning: {warning}
          </span>
          <span aria-hidden="true">{warning}</span>
        </Alert>
      );
      controlProps['aria-invalid'] = 'true';
      controlProps['aria-describedby'] = warningId;
    }
  }

  let controlElement;
  if (type === 'checkbox') {
    controlElement = (
      <div className="checkbox-container">
        <FormCheck
          type="checkbox"
          {...input}
          {...otherProps}
          {...controlProps}
          disabled={disable}
          label={<div className="checkbox-label">{labelNode || label}</div>}
        />
        {children}
      </div>
    );
    labelElement = null;
  } else if (type === 'radio-group') {
    // Using "radio-group" because redux-form has special behavior for "radio"
    // TODO How to handle label and IDs
    controlElement =
      children &&
      children.map(item => (
        <FormCheck
          type="radio"
          {...input}
          {...otherProps}
          key={item.value}
          value={item.value}
          disabled={disable}
          checked={item.value === input.value}
        >
          {item.label}
        </FormCheck>
      ));
  } else if (type === 'select') {
    controlElement = (
      <>
        <FormControl
          {...input}
          {...otherProps}
          {...controlProps}
          disabled={disable}
          as="select"
        >
          <option value="" disabled hidden>
            SELECT
          </option>
          {children &&
            children.map(({ value: val, label: itemLabel, ...itemRest }) => (
              <option {...itemRest} key={val} value={val}>
                {itemLabel}
              </option>
            ))}
        </FormControl>
        {icon}
      </>
    );
  } else {
    controlElement = (
      <FormControl
        type={type}
        disabled={disable}
        {...input}
        {...otherProps}
        {...controlProps}
      >
        {children}
      </FormControl>
    );
  }

  if (controlWrapperFn) {
    controlElement = controlWrapperFn(controlElement);
  }

  return (
    <div className={`input-container ${wrapperClass || ''}`}>
      <FormGroup validationState={validationState}>
        {labelElement && <div className="input-label">{labelElement}</div>}
        {controlElement}
        {subLabel}
        {helpBlock}
      </FormGroup>
    </div>
  );
}

Input.propTypes = {
  label: PropTypes.string.isRequired,
  labelNode: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string
  ]),
  subLabel: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string
  ]),
  type: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  validationState: PropTypes.string,
  hideErrors: PropTypes.bool,
  hideWarnings: PropTypes.bool
};

export default Input;
